import React, {useEffect, useRef, useState} from "react";
import {useData} from "../../../hooks/useData";
import {doFetch} from "../../../util/fetch";
import {GetPlayerResponse} from "../interfaces";
import SvgLoader from "../../../svg/SvgLoader";
import {relativeDate, relativeDuration} from "../../../util/timeConverter";
import {DEFAULT_STEAM_AVATAR_URL, steamAvatarUrl} from "../../../util/steamHelper";
import OverlayBase from "../overlay/OverlayBase";
import {useServerId} from "../useServerId";
import {useEvent} from "../../../hooks/useEvent";
import {useOverlay} from "../overlay/OverlayContext";
import {PlayerUpdateEvent} from "./PlayerContext";
import {LucideExternalLink, LucideMoreVertical} from "lucide-react";
import {useRPC, useSocket} from "../SocketContext";
import InventoryTab from "./inventory/InventoryTab";

interface PlayerOverlayProps {
    steamId: string
    hide: () => void
}

export default function PlayerOverlay(props: PlayerOverlayProps) {
    const serverId = useServerId()
    const socket = useSocket()

    const [n, setN] = useState<number>(0)
    const data = useData<GetPlayerResponse>(`/api/client/player?serverId=${serverId}&steamId=${props.steamId}&update=false&n=${n}`)

    useEvent<PlayerUpdateEvent>("playerUpdate", e => {
        if (!e || e.player.steamId !== props.steamId){
            return
        }
        setN(n => n + 1)
    })

    useRPC(socket, "OnPlayerAction", (action: string, steamId: string, text?: string) => {
        //console.log("OnPlayerAction (player overlay)", action, steamId, text)
        if (steamId === props.steamId){
            setN(n => n + 1)
        }
    })

    return (
        <OverlayBase hide={props.hide} w="w-[28rem]">
            {data.isLoading && (
                <div className="flex flex-col items-center">
                    <SvgLoader/>
                    <p className="mt-4 text-white text-lg font-semibold tracking-wide">Loading...</p>
                </div>
            )}
            {!data.isLoading && ((data.data && <PlayerContent info={data.data} steamId={props.steamId} onRefresh={() => setN(n => n + 1)}/>) || <p className="text-red-500">Failed to player: {data.error}</p>)}
        </OverlayBase>
    )
}

function PlayerContent({info, steamId, onRefresh}: { info: GetPlayerResponse, steamId: string, onRefresh: () => void }) {
    const overlay = useOverlay()
    const serverId = useServerId()
    const [tab, setTab] = useState<"overview" | "inventory">("overview")

    const player = info.player
    const ultraLite = { steamId, displayName: info.displayName, isBanned: info.isBanned }

    const deletePlayerHistory = () => {
        overlay.showConfirmation("Delete player history?", async () => {
            const result = await doFetch(`/api/client/player?serverId=${serverId}&steamId=${steamId}`, {
                method: "DELETE"
            })
            if (result.ok) {
                onRefresh()
                return undefined
            }
            return result.errorString ?? "Unknown error"
        }, "This will permanently delete all stored data and history for this player. This action cannot be undone.", "Delete")
    }

    const showSteamProfile = () => {
        const popupWidth = 1200;
        const popupHeight = 800;

        const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

        // eslint-disable-next-line no-restricted-globals
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || screen.width;
        // eslint-disable-next-line no-restricted-globals
        const windowHeight = window.innerHeight || document.documentElement.clientHeight || screen.height;

        const left = dualScreenLeft + (windowWidth - popupWidth) / 2;
        const top = dualScreenTop + (windowHeight - popupHeight) / 2;

        const features = `width=${popupWidth},height=${popupHeight},top=${top},left=${left},resizable=yes,scrollbars=yes`

        const popup = window.open(`https://steamcommunity.com/profiles/${steamId}`, "steam_popup", features);

        if (popup && popup.focus) {
            popup.focus();
        }
    }

    const copySteamId = async () => {
        await navigator.clipboard.writeText(steamId)
    }

    return (<div className="space-y-4">
        {/* HEAD */}
        <div className="flex flex-row align-top">
            <img className="rounded-md" src={player ? steamAvatarUrl(player.steamAvatarHash, 1) : DEFAULT_STEAM_AVATAR_URL} alt="Avatar"/>
            <div className="flex flex-col ms-2 select-text">
                {info.isMatch || info.displayName !== "unnamed"
                    ? <h3 className="text-xl font-bold">{info.displayName}</h3>
                    : <h3 className="text-xl font-bold italic text-gray-400">Unknown</h3>
                }
                <span>{steamId}</span>
            </div>
            {/* ... menu */}
            <PlayerMenu onGiveItem={() => overlay.openGiveItemOverlay(ultraLite)} onDelete={deletePlayerHistory}/>
        </div>

        {/* TABS */}
        <div className="flex flex-row gap-1 border-b border-neutral-600">
            <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>Overview</TabButton>
            <TabButton active={tab === "inventory"} onClick={() => setTab("inventory")}>Inventory</TabButton>
        </div>

        {/* OVERVIEW TAB */}
        {tab === "overview" && <div className="space-y-4">
            {/* quick actions */}
            <div>
                <h3 className="mb-2 text-xl font-bold">Quick actions</h3>
                <div className="flex flex-row flex-wrap gap-2 leading-tight">
                    <button className="px-4 py-1 rounded-md bg-primary text-white hover:bg-primary-hover flex items-center" onClick={showSteamProfile}><LucideExternalLink size={16} className="me-2" /> Steam Profile</button>
                    <button className="px-4 py-1 rounded-md bg-primary text-white hover:bg-primary-hover " onClick={copySteamId}>Copy Steam ID</button>
                    {player && player.isConnected && <button className="px-4 py-1 rounded-md bg-yellow-500 text-black hover:opacity-90"
                             onClick={() => overlay.openKickOverlay(player)}>Kick</button>}
                    {info.isBanned ?
                    <button className="px-4 py-1 rounded-md bg-red-500 text-white hover:opacity-90" onClick={() => overlay.openUnbanOverlay(ultraLite)}>Unban</button>
                    :
                    <button className="px-4 py-1 rounded-md bg-red-500 text-white hover:opacity-90" onClick={() => overlay.openBanOverlay(ultraLite)}>Ban</button>
                    }
                    {/*{player.ip && <button className="px-4 py-1 rounded-md bg-red-500 text-white hover:opacity-90"*/}
                    {/*         onClick={() => overlay.openIpBanOverlay(player.ip)}>Ban IP</button>}*/}
                </div>
            </div>
            {/* general info */}
            {player && <div>
                <h3 className="mb-2 text-xl font-bold">General</h3>
                <table className="w-full">
                    <tbody>
                    <tr>
                        <td className="font-semibold">Status</td>
                        <td className="select-text ps-2 inline-flex items-baseline"><span
                            className={"block me-1 size-3 rounded-full " + (player.isConnected ? "bg-green-500" : "bg-red-500")}></span>{player.isConnected ? "Connected" : "Not connected"}
                        </td>
                    </tr>
                    {player.isConnected && <tr>
                        <td className="font-semibold">Current session</td>
                        <td className="select-text ps-2">{relativeDuration(player.sessionPlayTime)}</td>
                    </tr>}
                    <tr>
                        <td className="font-semibold">Server play time</td>
                        <td className="select-text ps-2">{relativeDuration(player.totalPlayTime)}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">Last seen</td>
                        <td className="select-text ps-2">{player.isConnected ? "now" : relativeDate(player.lastSeen)}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">First seen</td>
                        <td className="select-text ps-2">{relativeDate(player.firstSeen)}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">IP</td>
                        <td className="select-text ps-2">{player.ip}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">Team ID</td>
                        <td className="select-text ps-2">{player.teamId === undefined ? <i>Plugin not installed</i> :
                            player.teamId > 0 ? player.teamId : <i>No team</i>}</td>
                    </tr>
                    </tbody>
                </table>
            </div>}
            {/* Team */}
            {info.team && <div>
                <h3 className="mt-4 mb-2 text-xl font-bold">Team Members</h3>
                <div className="flex flex-col mt-2 [&>*]:first-child:border-t">
                    {info.team.members.map(p => (<div key={p.steamId} className="">
                        <div className="flex flex-row items-center space-x-2 ">
                            <span
                                className={"size-3 rounded-full inline-block " + (p.isConnected ? "bg-green-500" : "bg-neutral-600")}></span>
                            <b>{p.displayName}</b>
                        </div>
                        <p>{p.steamId}</p>
                    </div>))}
                </div>
            </div>}
            {/* Steam info */}
            {player && <div>
                <h3 className="mt-4 mb-2 text-xl font-bold">Steam account info</h3>
                <table className="w-full">
                    <tbody>
                    <tr>
                        <td className="font-semibold">Display name</td>
                        <td className="select-text ps-2">{player.steamDisplayName}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">Game Bans</td>
                        <td className="select-text ps-2">{player.steamGameBans}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">VAC Bans</td>
                        <td className="select-text ps-2">{player.steamVacBans}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">Last Ban</td>
                        <td className="select-text ps-2">{player.steamLastBanTime > 0 ? relativeDate(player.steamLastBanTime, undefined, true) : (
                            <i>No bans on record</i>)}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">Account created</td>
                        <td className="select-text ps-2">{relativeDate(player.steamAccountCreated)}</td>
                    </tr>
                    <tr>
                        <td className="font-semibold">Aliases</td>
                        <td className="select-text ps-2">{player.steamAliases.length > 0 ? player.steamAliases.join(', ') : (
                            <i>No known aliases</i>)}</td>
                    </tr>
                    </tbody>
                </table>
            </div>}
        </div>}

        {/* INVENTORY TAB */}
        {tab === "inventory" && <InventoryTab steamId={steamId} displayName={info.displayName}/>}
    </div>)
}

function TabButton({active, onClick, children}: { active: boolean, onClick: () => void, children: React.ReactNode }) {
    return (
        <button onClick={onClick}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${active ? "border-primary text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>
            {children}
        </button>
    )
}

function PlayerMenu({ onGiveItem, onDelete }: { onGiveItem: () => void, onDelete: () => void }) {
    const menuRef = useRef<HTMLDivElement>(null)
    const [showMenu, setShowMenu] = useState<boolean>(false)

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    return (
        <div ref={menuRef} className="absolute top-4 right-12 z-10">
            <button className="p-1 rounded-md text-white hover:bg-neutral-700" onClick={() => setShowMenu(v => !v)} aria-label="More options">
                <LucideMoreVertical size={20}/>
            </button>
            {showMenu && <div className="absolute right-0 mt-1 w-52 origin-top-right rounded-md bg-neutral-600 text-white text-sm overflow-hidden shadow-lg">
                <button className="block w-full text-left px-4 py-2 hover:bg-neutral-700" onClick={() => { setShowMenu(false); onGiveItem() }}>Give Item</button>
                <button className="block w-full text-left px-4 py-2 text-red-400 hover:bg-neutral-700 border-t border-neutral-500" onClick={() => { setShowMenu(false); onDelete() }}>Delete player history</button>
            </div>}
        </div>
    )
}
